import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

export default function NotesList({ eventId, canEdit }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const { user } = useAuth();

  const fetchNotes = useCallback(async () => {
    const { data, error } = await supabase
      .from('event_notes')
      .select(`
        id, 
        content, 
        created_at,
        created_by
      `)
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des notes:', error);
      return;
    }

    // Récupérer les full_name des utilisateurs liés
    const userIds = [...new Set(data.map(n => n.created_by).filter(Boolean))];
    let usersMap = {};
    if (userIds.length > 0) {
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, full_name')
        .in('id', userIds);
      if (!usersError && usersData) {
        usersMap = Object.fromEntries(usersData.map(u => [u.id, u.full_name]));
      }
    }

    // Associer le nom complet à chaque note
    const notesWithNames = data.map(n => ({
      ...n,
      full_name: usersMap[n.created_by] || n.created_by
    }));

    setNotes(notesWithNames);
  }, [eventId]);

  useEffect(() => {
    if (eventId) fetchNotes();
  }, [eventId, fetchNotes]);

  if (!eventId) return null;

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    const { error } = await supabase
      .from('event_notes')
      .insert({
        event_id: eventId,
        content: newNote,
        created_by: user.id
      });

    if (error) {
      console.error('Erreur lors de l\'ajout de la note:', error);
      return;
    }

    setNewNote('');
    fetchNotes();
  };

  const handleDeleteNote = async (noteId) => {
    const { error } = await supabase
      .from('event_notes')
      .delete()
      .eq('id', noteId);

    if (error) {
      console.error('Erreur lors de la suppression de la note:', error);
      return;
    }

    fetchNotes();
  };

  return (
    <Box>
      {canEdit && (
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Ajouter une note..."
            sx={{ mb: 1 }}
          />
          <Button
            variant="contained"
            onClick={handleAddNote}
            disabled={!newNote.trim()}
          >
            Ajouter
          </Button>
        </Box>
      )}

      <List>
        {notes.map((note) => (
          <ListItem key={note.id} divider>
            <ListItemText
              primary={note.content}
              secondary={
                <Typography variant="caption">
                  {`Ajouté par ${note.full_name}`} - {format(new Date(note.created_at), 'dd/MM/yyyy HH:mm')}
                </Typography>
              }
            />
            {canEdit && note.created_by === user.id && (
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => handleDeleteNote(note.id)}>
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
